import React, { useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { get, capitalize } from 'lodash';
import { connect } from 'react-redux';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';

// Models
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import {
  TypeRankingsModel,
  buildTypeRankingsPath,
  buildTypeRankingsPathForBelowTheFold,
  buildTypeRankingsPathForAboveTheFold,
} from 'client/data/models/type-rankings';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT, transform } from 'client/data/cms/content';

// Utils
import { TrackingHandler } from 'client/tracking/handler';
import {
  getBreadcrumbName,
  TYPE_TITLE_LOWERCASE_EXCEPTIONS_MAPPING,
  getUserFriendlyTypeName,
} from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { getCustomTitle } from 'client/site-modules/upper-funnel/utils/seo-content';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { handlePixelData } from 'site-modules/shared/components/jelly-beans/jelly-bean-tracker';
import { generateSrpLinkNewType } from 'site-modules/shared/utils/srp-link-constructor';
import { getTypeFaqsContent } from 'site-modules/upper-funnel/utils/type-faqs-generator';
import { scrollToHash } from 'client/utils/scroll';
import { getPlural } from 'client/utils/plural';
import { getRankingsContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';

// Constants
import { TrackingMap } from 'client/tracking/maps/upper-funnel/type/type';
import { CAR_TYPES, PAGE_CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';

// Shared components
import { withCompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { SuggestedVehiclePricingCarouselContainer } from 'site-modules/shared/components/suggested-vehicle-pricing-carousel/suggested-vehicle-pricing-carousel-container';
import { Link } from 'site-modules/shared/components/link/link';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { VideoTypeModule } from 'site-modules/shared/components/video-module/video-module';
import { EmbeddedYoutube } from 'site-modules/shared/components/video/embedded-youtube/embedded-youtube';
import { TypeNewsWidget } from 'client/site-modules/shared/components/news-widget/news-widget';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { TypeJellybeanNavV2 } from 'site-modules/shared/components/type-jellybean-nav/type-jellybean-nav-v2';
import { ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { TypeIconSymbolSheet } from 'site-modules/shared/components/upper-funnel/type-icon-symbol-sheet/type-icon-symbol-sheet';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

// Components
import { VehicleTypeRankingsList } from 'site-modules/upper-funnel/components/type/vehicle-type-rankings-list/vehicle-type-rankings-list';
import { SuggestedArticleLinksV2 } from 'site-modules/upper-funnel/components/type/suggested-article-links/suggested-article-links-v2';
import { MakeTypeList } from 'site-modules/upper-funnel/components/type/make-type-list/make-type-list';
import { AnchorNav } from 'site-modules/upper-funnel/components/type/anchor-nav/anchor-nav';
import { AnchorNavDropdown } from 'site-modules/upper-funnel/components/type/anchor-nav/anchor-nav-dropdown';

import { MostSoldTypeInfographic } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/most-sold-type-infographic';
import { TYPES_WITH_MOST_SOLD_GRAPH } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/constants';
import { RankingsSummaryLists } from 'site-modules/upper-funnel/components/rankings-summary-lists/rankings-summary-lists';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { TopRatedPromoUnit } from 'site-modules/shared/components/top-rated-promo-unit/top-rated-promo-unit';
import { HeroContent } from 'site-modules/upper-funnel/components/type/hero-content/hero-content';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';
import { HomeModulesModel } from 'client/data/models/home-modules';

import './type.scss';
/* LAZY IMPORTS */
import lazyImports from './lazy-imports';

const { FaqLazy } = lazyImports;

const VehicleTypeRankingsListWithCompareTray = withCompareVehicleTray(VehicleTypeRankingsList, {
  backFocusLink: {
    label: 'Skip to vehicle rankings',
    selector: '.vehicle-card .compare-checkbox input',
  },
});

const TYPES_WITHOUT_SRP = [
  CAR_TYPES.EXOTIC,
  CAR_TYPES.ECONOMY,
  CAR_TYPES.SUBCOMPACT,
  CAR_TYPES.MIDSIZE,
  CAR_TYPES.MUSCLE,
  CAR_TYPES.COMPACT,
  CAR_TYPES.SPORTS_CAR,
  CAR_TYPES.CROSSOVER,
];

const EV_LINKS = [
  {
    to: '/electric-car/articles/',
    label: 'Edmunds EV Hub',
    description: 'Learn more about EVs with our latest research, tips and advice',
    iconClassName: 'icon-power2',
    id: 'ev-knowledge-center',
  },
  {
    to: '/electric-car/tax-credits-rebates-incentives/',
    label: 'Search EV tax credits & rebates',
    description: 'Find electric vehicle discounts available for you',
    iconClassName: 'icon-coins',
    id: 'search-ev-tax-credits',
  },
  {
    to: '/electric-car/articles/ev-buying-guide/',
    label: 'EV Buying Guide',
    description: 'Considering an electric vehicle? Our guide can help at any stage',
    iconClassName: 'icon-battery_charging_full2',
    id: 'ev-buying-guide',
  },
];

const SUV_LINKS = [
  {
    to: '/suv/articles/suv-buying-guide/',
    label: 'SUV Buying Guide',
    description: "We'll walk you through all the different kinds of SUV and find the right one for you.",
    iconClassName: 'icon-car',
    id: 'suv-buying-guide',
  },
];

const TRUCK_LINKS = [
  {
    to: '/truck/articles/truck-buying-guide/',
    label: 'Truck Buying Guide',
    description: 'Shopping for a pickup?  We make it easy to find the truck that’s right for you.',
    iconClassName: 'icon-truck',
    id: 'truck-buying-guide',
  },
];

const HYBRID_LINKS = [
  {
    to: '/hybrid/articles/hybrid-buying-guide/',
    label: 'Hybrid Buying Guide',
    description: "We'll walk you through all the different kinds of hybrids and find the right one for you.",
    iconClassName: 'icon-hybrid_mpg',
    id: 'hybrid-buying-guide',
  },
];

const SHOPPING_LINKS = {
  [CAR_TYPES.ELECTRIC]: EV_LINKS,
  [CAR_TYPES.SUV]: SUV_LINKS,
  [CAR_TYPES.TRUCK]: TRUCK_LINKS,
  [CAR_TYPES.HYBRID]: HYBRID_LINKS,
};

const getRefererPathName = referer => (referer ? get(referer.match(/edmunds.com(.*)/), '[1]', '') : '');

export function TypePageV2UI(props) {
  const impressionRef = useRef();

  const {
    type,
    isMobile,
    vehicleEditorialRankings,
    location,
    referer,
    faqContent,
    enableMarketBrewTestBestType,
    vehicleEditorialRankingsBelowTheFold,
    vehicleEditorialRankingsAboveTheFold,
    vehicleShowcaseContent,
  } = props;

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
    handlePixelData.init(impressionRef, location);
    handlePixelData.removeSessionItem();

    scrollToHash(get(location, 'hash', ''), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const breadcrumbsData = [getHomeBreadcrumb(), { title: getBreadcrumbName(type) }];
  const trkParam = getRefererPathName(referer) === '/' ? { trk: type } : {};
  const faqEntries = getTypeFaqsContent({
    vehicleEditorialRankings: [...vehicleEditorialRankingsAboveTheFold, ...vehicleEditorialRankingsBelowTheFold],
    faqContent,
    typeSlug: type,
  });

  const title = getCustomTitle(type);
  const hasMostSoldTypeContent = TYPES_WITH_MOST_SOLD_GRAPH.includes(type);
  const mostSoldTypeGraphTitle = hasMostSoldTypeContent
    ? `Most Popular ${getPlural(getUserFriendlyTypeName(type), undefined, undefined, true)}`
    : null;
  const className = type === CAR_TYPES.SUV ? 'type-anchor-nav mt-0' : 'type-anchor-nav mb-1 mt-0';
  const containerClassName = 'container px-0';
  const navSuvList = [
    { name: 'Hybrid SUVs', hash: 'hybrid' },
    { name: 'Electric SUVs', hash: 'electric' },
    { name: '3-Row SUVs', hash: '3-row' },
  ];

  return (
    <main id="main-content" className="type-page-v2" role="main" aria-labelledby="type-page-heading">
      <HeroContent breadcrumbsData={breadcrumbsData} isMobile={isMobile} type={type} title={title} />
      <Container className="mb-2">
        <TopRatedPromoUnit className="my-2" isMobile={isMobile} />
        <div className="card-link-full-hitarea not-card-link pos-s d-none" />
        <div className="pt-sm-1_5 pb-1_25 px-0_5 px-sm-0">
          <AnchorNavDropdown
            vehicleEditorialRankings={vehicleEditorialRankings}
            mostSoldTypeGraphTitle={mostSoldTypeGraphTitle}
            type={type}
            className={className}
            containerClassName={containerClassName}
            isMobile={isMobile}
            hasSeparator={false}
          />
          {type === CAR_TYPES.SUV && (
            <div style={{ height: '54px' }}>
              <AnchorNav
                navSuvList={navSuvList}
                isMobile={isMobile}
                className="pt-1"
                containerClassName={containerClassName}
                titleClassName="heading-5 pe-1 py-0"
                subLinkClassName="pt-0_25"
                subArrowClassName="pt-0_25 pb-1"
                type={type}
                title="Read next:"
                ariaLabel="go to page"
                noScroll
                hideSticky
                hasSeparator={false}
                useItemClassName={false}
                useNoFollow={false}
              />
            </div>
          )}
        </div>
        <div>
          {SHOPPING_LINKS[type]?.length && (
            <Row className="mb-2">
              {SHOPPING_LINKS[type].map(link => {
                const { to, label, description, iconClassName, id } = link;
                return (
                  <Col xs={12} lg={Math.min(6, 12 / SHOPPING_LINKS[type].length)} className="d-flex" key={to}>
                    <ShoppingLink
                      to={to}
                      label={label}
                      description={description}
                      className="rounded px-1 px-md-1_5 py-0_75 py-md-1 bg-white mb-1 w-100"
                      descriptionClassName="mt-0_25"
                      iconClassName={iconClassName}
                      iconContainerClassName="me-0_75 me-md-1_5 size-16 text-blue-50"
                      iconStyles={{ height: '48px', width: '48px' }}
                      data-tracking-id="link_select_tool_select"
                      id={id}
                    />
                  </Col>
                );
              })}
            </Row>
          )}
          <VehicleTypeRankingsListWithCompareTray
            type={type}
            trkParam={trkParam}
            vehicleEditorialRankings={[
              ...vehicleEditorialRankingsAboveTheFold,
              ...vehicleEditorialRankingsBelowTheFold,
            ]}
          />
        </div>
      </Container>
      {type !== PAGE_CAR_TYPES.MINIVAN && (
        <VehicleShowcase
          content={vehicleShowcaseContent}
          useContainer
          isMediumLayout={false}
          bottomBorder={false}
          className="mt-2 mb-3_5"
        />
      )}
      <Container>
        <div className="d-flex flex-column">
          <Row
            className="mx-0_25 mx-md-0 px-1 px-xl-0_75 pt-1_75 pb-md-1_75 bg-white border-radius-12"
            style={{ marginBottom: '72px' }}
          >
            <Col xs={12} lg={8} className="ps-0">
              <TypeIconSymbolSheet />
              <TypeJellybeanNavV2
                type={type}
                isMobile={isMobile}
                enableMarketBrewTestBestType={enableMarketBrewTestBestType}
              />
            </Col>
            <Col
              xs={12}
              lg={4}
              className="ps-0 ps-lg-2 ps-xl-2_5 pe-0 pt-1_25 pt-md-1_75 pt-lg-0 suggested-article-links-wrapper-v2"
            >
              <SuggestedArticleLinksV2 type={type} />
            </Col>
          </Row>
          {/* For correct ads positioning */}
          {isMobile && <div aria-hidden />}
        </div>
        <div>
          {hasMostSoldTypeContent && (
            <ScrollElement id={`best-selling-${type}s`}>
              <RenderWhenViewable
                verticalOffset="100%"
                placeholder={<MostSoldTypeInfographic type={type} isMobile={isMobile} showMap={false} />}
              >
                <MostSoldTypeInfographic type={type} isMobile={isMobile} />
              </RenderWhenViewable>
            </ScrollElement>
          )}
          <VideoTypeModule
            type={type}
            pageName="new_type_index"
            playlistClassName="mt-1_5 mt-lg-0"
            withSchemaMeta={false}
            videoLargeColumnSize={8}
            playlistLargeColumnSize={4}
            isMobile={isMobile}
            playerComponent={EmbeddedYoutube}
            lazyLoadPreview
          />
        </div>
        <div>
          {Boolean(faqEntries && faqEntries.length) && (
            <Fragment>
              <FaqLazy
                title="FAQ"
                entries={faqEntries}
                pathname={location.pathname}
                withTracking
                isOpenCollapseOnLoad={false}
              />
              <hr className="mt-0 mb-2" />
            </Fragment>
          )}
          {[CAR_TYPES.SEDAN, CAR_TYPES.TRUCK].includes(type) && (
            <Fragment>
              <RankingsSummaryLists
                type={capitalize(type)}
                vehicleEditorialRankings={[
                  ...vehicleEditorialRankingsAboveTheFold,
                  ...vehicleEditorialRankingsBelowTheFold,
                ]}
              />
              <hr className="mt-1_5 mb-2" />
            </Fragment>
          )}
          <MakeTypeList type={type} />
          {!TYPES_WITHOUT_SRP.includes(type) && (
            <Link
              to={generateSrpLinkNewType(type)}
              className="text-primary-darker d-inline-block"
              data-tracking-id="view_search_result"
            >
              See new {TYPE_TITLE_LOWERCASE_EXCEPTIONS_MAPPING[type] || type}s for sale{' '}
              <span className="icon-arrow-right4 xsmall" aria-hidden />
            </Link>
          )}
        </div>
        {type === CAR_TYPES.ELECTRIC && (
          <TreehousePromotionCheck>
            <Row className="my-3_5">
              <Col xs={12} md={8}>
                <ReadyForEvTreehouse
                  className="bg-white rounded-12 px-1_5 py-1_5"
                  heading="Am I Ready for an EV?"
                  headingTag="h2"
                  creativeId="treehouse-promotion"
                />
              </Col>
            </Row>
          </TreehousePromotionCheck>
        )}
      </Container>
      <hr id="impression-tracker-container" className="d-none" ref={impressionRef} />
      <MinHeightWrapper className="suggested-pricing-wrapper" componentType="SUGGESTED_VEHICLE_PRICING_CAROUSEL">
        <SuggestedVehiclePricingCarouselContainer />
      </MinHeightWrapper>
      <Container className="pb-1">
        <TypeNewsWidget type={type} withDivider />
        <hr className="mt-1_5 mb-2" />
        <PageLinksWidget className="mb-3_5" divideSections={false} title="Related information" />
      </Container>
      <Experiment name="ads-11082" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal" />
      </Experiment>
    </main>
  );
}

TypePageV2UI.propTypes = {
  type: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
  vehicleEditorialRankings: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  vehicleEditorialRankingsBelowTheFold: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  vehicleEditorialRankingsAboveTheFold: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  referer: PropTypes.string,
  faqContent: CmsEntities.Content,
  enableMarketBrewTestBestType: PropTypes.bool,
  vehicleShowcaseContent: CmsEntities.Content,
};

TypePageV2UI.defaultProps = {
  isMobile: false,
  vehicleEditorialRankings: [],
  vehicleEditorialRankingsBelowTheFold: [],
  vehicleEditorialRankingsAboveTheFold: [],
  referer: '',
  faqContent: DEFAULT_CONTENT,
  enableMarketBrewTestBestType: false,
  vehicleShowcaseContent: DEFAULT_CONTENT,
};

export const stateToPropsConfig = {
  vehicleEditorialRankings: bindToPath(({ type }) => buildTypeRankingsPath(type), TypeRankingsModel),
  vehicleEditorialRankingsAboveTheFold: bindToPath(
    ({ type }) => buildTypeRankingsPathForAboveTheFold(type),
    TypeRankingsModel
  ),
  vehicleEditorialRankingsBelowTheFold: bindToPath(
    ({ type }) => buildTypeRankingsPathForBelowTheFold(type),
    TypeRankingsModel
  ),
  faqContent: bindToContent(({ type }) => getRankingsContentPath(type), 'faq'),
  vehicleShowcaseContent: bindToPath('vehicleShowcaseContent', HomeModulesModel, transform),
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  referer: get(state.pageContext.page, 'referer'),
  enableMarketBrewTestBestType: !!get(state, 'featureFlags["enable-market-brew-test-best-type"]'),
});

export const TypePageV2 = connect(mapStateToProps)(connectToModel(TypePageV2UI, stateToPropsConfig));
